import * as React from 'react';

export interface Prefs {
  inkColor: string;
  inkWidth: number;
  fingerDrawing: boolean;
  leftHanded: boolean;
  template: 'lined' | 'grid' | 'blank';
  theme: 'system' | 'light' | 'dark';
  referenceSplit: number;
  recallStudySeconds: number;
  recallStrict: boolean;
}

const DEFAULTS: Prefs = {
  inkColor: '#1b2a4a',
  inkWidth: 3.2,
  fingerDrawing: true,
  leftHanded: false,
  template: 'lined',
  theme: 'system',
  referenceSplit: 0.34,
  recallStudySeconds: 120,
  recallStrict: false,
};

const KEY = 'gn.prefs';
const listeners = new Set<() => void>();
let current: Prefs = load();

function load(): Prefs {
  try {
    const stored = JSON.parse(localStorage.getItem(KEY) ?? '{}') as Partial<Prefs>;
    // 0.4 was the old default (never dragged): give the notebook the new, wider share.
    if (stored.referenceSplit === 0.4) delete stored.referenceSplit;
    return { ...DEFAULTS, ...stored };
  } catch {
    return DEFAULTS;
  }
}

export function setPrefs(patch: Partial<Prefs>) {
  current = { ...current, ...patch };
  try {
    localStorage.setItem(KEY, JSON.stringify(current));
  } catch {
    /* storage unavailable: keep in memory */
  }
  applyTheme(current.theme);
  listeners.forEach((l) => l());
}

export function applyTheme(theme: Prefs['theme']) {
  const el = document.documentElement;
  if (theme === 'system') el.removeAttribute('data-theme');
  else el.setAttribute('data-theme', theme);
}

/** Per-device conveniences (pen, layout). Never used for data that must persist. */
export function usePrefs(): [Prefs, (p: Partial<Prefs>) => void] {
  const p = React.useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => current,
    () => current,
  );
  return [p, setPrefs];
}
